<template>
  <div
    class="ms-switch"
    :class="{ 'is-disabled': switchDisabled, 'is-checked': checked }"
    role="switch"
    :aria-checked="checked"
    title=""
    :aria-disabled="switchDisabled"
    @click.prevent="switchValue"
  >
    <input
      :id="id"
      ref="input"
      class="ms-switch__input"
      type="checkbox"
      :name="name"
      :true-value="activeValue"
      :false-value="inactiveValue"
      :disabled="switchDisabled"
      @change="handleChange"
      @keydown.enter="switchValue"
    >
    <span
      v-if="inactiveIconClass || inactiveText"
      :class="['ms-switch__label', 'ms-switch__label--left', !checked ? 'is-active' : '']"
    >
      <i
        v-if="inactiveIconClass"
        :class="[inactiveIconClass]"
      />
      <span
        v-if="!inactiveIconClass && inactiveText"
        :aria-hidden="checked"
      >{{ inactiveText }}</span>
    </span>
    <span
      ref="core"
      class="ms-switch__core"
      :style="{ 'width': (width || 40) + 'px' }"
    >
      <div class="ms-switch__action">
        <!-- TODO: Xử lý icon loading -->
        <i
          v-if="loading"
          class=""
        />
      </div>
    </span>
    <span
      v-if="activeIconClass || activeText"
      :class="['ms-switch__label', 'ms-switch__label--right', checked ? 'is-active' : '']"
    >
      <i
        v-if="activeIconClass"
        :class="[activeIconClass]"
      />
      <span
        v-if="!activeIconClass && activeText"
        :aria-hidden="!checked"
      >{{ activeText }}</span>
    </span>
  </div>
</template>
<script>
import { defineComponent, computed, onMounted, ref, nextTick, watch } from 'vue'
import { isPromise } from '@vue/shared'
import { isBool } from '@/components/utils/util'
import throwError, { warn } from '@/components/utils/error'
import { UPDATE_MODEL_EVENT, CHANGE_EVENT, INPUT_EVENT } from '@/components/utils/constants'

export default defineComponent({
  name: 'MsSwitch',

  props: {
    modelValue: {
      type: [Boolean, String, Number],
      default: false,
    },
    value: {
      type: [Boolean, String, Number],
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    width: {
      type: Number,
      default: 40,
    },
    activeIconClass: {
      type: String,
      default: '',
    },
    inactiveIconClass: {
      type: String,
      default: '',
    },
    activeText: {
      type: String,
      default: '',
    },
    inactiveText: {
      type: String,
      default: '',
    },
    activeColor: {
      type: String,
      default: '',
    },
    inactiveColor: {
      type: String,
      default: '',
    },
    activeValue: {
      type: [Boolean, String, Number],
      default: true,
    },
    inactiveValue: {
      type: [Boolean, String, Number],
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    record: {
      type: Object,
      default: null,
    },

    beforeChange: {
      type: Function,
      default: () => {
        return true
      },
    },
  },

  emits: [UPDATE_MODEL_EVENT, CHANGE_EVENT, INPUT_EVENT],
  setup (props, { emit }) {
    const isModelValue = ref(props.modelValue !== false)
    const input = ref(null)
    const core = ref(null)

    const scope = 'MsSwitch'

    watch(() => props.modelValue, () => {
      isModelValue.value = true
    })

    watch(() => props.value, () => {
      isModelValue.value = false
    })

    const actualValue = computed(() => {
      return isModelValue.value ? props.modelValue : props.value
    })

    const checked = computed(() => {
      return actualValue.value === props.activeValue
    })

    if (!~[props.activeValue, props.inactiveValue].indexOf(actualValue.value)) {
      emit(UPDATE_MODEL_EVENT, props.inactiveValue)
      emit(CHANGE_EVENT, props.inactiveValue)
      emit(INPUT_EVENT, props.inactiveValue)
    }

    watch(checked, () => {
      input.value.checked = checked.value

      if (props.activeColor || props.inactiveColor) {
        setBackgroundColor()
      }

      // if (props.validateEvent) {
      //   elFormItem.formItemMitt?.emit('el.form.change', [actualValue.value])
      // }
    })

    const switchDisabled = computed(() => {
      return props.disabled || props.loading
    })

    const handleChange = () => {
      const val = checked.value ? props.inactiveValue : props.activeValue
      emit(UPDATE_MODEL_EVENT, val)
      emit(CHANGE_EVENT, val, props.record)
      emit(INPUT_EVENT, val)
      nextTick(() => {
        input.value.checked = checked.value
      })
    }

    const switchValue = () => {
      if (switchDisabled.value) return

      const { beforeChange } = props
      if (!beforeChange) {
        handleChange()
        return
      }

      const shouldChange = beforeChange()

      const isExpectType = [isPromise(shouldChange), isBool(shouldChange)].some(i => i)
      if (!isExpectType) {
        throwError(scope, 'beforeChange must return type `Promise<boolean>` or `boolean`')
      }

      if (isPromise(shouldChange)) {
        shouldChange.then(result => {
          if (result) {
            handleChange()
          }
        }).catch(e => {
          if (process.env.NODE_ENV !== 'production') {
            warn(scope, `some error occurred: ${e}`)
          }
        })
      } else if (shouldChange) {
        handleChange()
      }
    }

    const setBackgroundColor = () => {
      const newColor = checked.value ? props.activeColor : props.inactiveColor
      const coreEl = core.value
      coreEl.style.borderColor = newColor
      coreEl.style.backgroundColor = newColor
      coreEl.children[0].style.color = newColor
    }

    const focus = () => {
      input.value?.focus?.()
    }

    onMounted(() => {
      if (props.activeColor || props.inactiveColor) {
        setBackgroundColor()
      }

      input.value.checked = checked.value
    })

    return {
      input,
      core,
      switchDisabled,
      checked,
      handleChange,
      switchValue,
      focus,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/MsSwitch.scss';
</style>
